import { z } from "zod";

import { POSTAL_CODE_REGEX } from "@luce/core/constants";

export const addressSchema = z.object({
  label: z.string().max(30, "Label must be at most 30 characters").optional(),
  postalCode: z
    .string()
    .min(1, "Postal code is required")
    .length(6, "Postal code must be exactly 6 digits")
    .regex(POSTAL_CODE_REGEX, "Postal code must only contain digits"),
  fullAddress: z.string().min(1, "Full address is required"),
  unitFloor: z.string().min(1, "If not applicable, enter 0 for Floor or Apt No."),
  apartmentNumber: z.string().min(1, "If not applicable, enter 0 for Floor or Apt No."),
  primary: z.boolean(),
});

export type AddressFormValues = z.input<typeof addressSchema>;

export const emptyAddressValues: AddressFormValues = {
  label: "",
  postalCode: "",
  fullAddress: "",
  unitFloor: "",
  apartmentNumber: "",
  primary: false,
};

/** Pack the floor + apartment inputs into the backend's `#floor-apt` unit string. */
export function createUnitNumber(unitFloor?: string, apartmentNumber?: string): string {
  const floor = unitFloor ?? "";
  const apt = apartmentNumber ?? "";

  if ((floor === "" && apt === "") || (floor === "0" && apt === "0")) return "";
  if (floor === "") return `#NA-${apt}`;
  if (apt === "") return `#${floor}-NA`;

  return `#${floor}-${apt}`;
}

/** Unpack a `#floor-apt` unit string back into separate floor + apartment inputs. */
export function getUnitNumber(unitNumber?: string | null): {
  unitFloor: string;
  apartmentNumber: string;
} {
  if (!unitNumber) return { unitFloor: "", apartmentNumber: "" };

  const [floorPart = "", apartmentNumber = ""] = unitNumber.split("-");
  const unitFloor = floorPart.split("#")[1] ?? floorPart;

  return { unitFloor, apartmentNumber };
}

/** Trim an optional text field; empty becomes `undefined` so it is omitted from the mutation. */
export function sanitizeOptionalText(value?: string | null): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}
